import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { thunkApiCallGenerator, apiCall } from '../APICalls/APICalls';
import { deepCopy } from './quotesAndNotificationsSlice';

const initialState = {
    //loading: 'loading',
    loading: 'none',
    isAuthenticated: false,
    // Until the session check on startup finishes we do not know whether the
    // user is signed in, and must show neither the app nor the login form -
    // otherwise a refresh flashes the login screen at an authenticated user.
    authChecked: false,
    // Set when a session ends underneath the user, so the login screen can say
    // why they are looking at it again.
    sessionMessage: '',
    permissions: [
        "Quotes.View Quotes",
        "Notifications.Notifications",
        "Create Quote.Create Quote",
        "Create Quote.Create Quote Revision",
        "Quotes.Print Quotes",
        "Quotes.Export Quote",
        "Quotes.Approve Quote",
        "Quotes.Approve On Behalf",
        "Quotes.Suggest Modifications",
        "Admin Portal.Create User Type",
        "Admin Portal.Edit User Type",
        "Admin Portal.Edit User",
        "Admin Portal.View All Users",
        "Admin Portal.Create Role"
    ],
    credentials: {
        userName: '',
        userId: '',
        email: '',
        userType: {
            userType: '',
            permissions: [],
            pages: [],
            role: ''
        },
        userStatus: '',
        homeAccountId: '',
        // True for a seeded or admin-reset account. The server refuses every
        // other endpoint until a new password is set.
        mustChangePassword: false
    },
    pages: {
        'Admin Portal': false,
        'Create Quote': false,
        'Notifications': false,
        'Quotes': false,
        'Pending': false,
        'Deactivated': false
    },
    subTitles: {
        'Admin Portal': 'Manage users and user types',
        'Create Quote': 'Please provide the necessary information to generate a quote',
        'Notifications': 'Please select a notification to view',
        'Quotes': 'Please select a quote to view',
        'Pending': 'Account is pending admin approval',
        'Deactivated': 'Account has been deactivated'
    },
    adminPortalView: {
        users: true,
        userTypes: false
    },
    currentSubTitle: '',
    sidePanelTitle: '',
    sidePanelOptions: [],
    userStatusLoading: 'complete'
};

// Redux Toolkit allows us to write "mutating" logic in reducers. It
// doesn't actually mutate the state because it uses the Immer library,
// which detects changes to a "draft state" and produces a brand new
// immutable state based off those changes
// The `reducers` field lets us define reducers and generate associated actions
// Use the PayloadAction type to declare the contents of `action.payload`

export const getUserTypeAndPermissions = thunkApiCallGenerator('pageLayout/getUserTypeAndPermissions');
export const putUser = thunkApiCallGenerator('pageLayout/putUser');
export const putUserStatus = thunkApiCallGenerator('pageLayout/putUserStatus');
export const userLogin = thunkApiCallGenerator('pageLayout/userLogin');

/**
 * Re-establishes the session on page load.
 *
 * The client persists no identity of its own - there is no token in
 * localStorage to read back, by design, because anything script can read
 * script can also steal. The only evidence of a session is an httpOnly
 * cookie the browser sends automatically, so asking the server is the only
 * way to find out who we are.
 */
export const restoreSession = createAsyncThunk(
    'pageLayout/restoreSession',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiCall('auth/me', null, 'GET');
            return response.data;
        } catch (error) {
            // A 401 here is the normal "not signed in" case, not a failure.
            return rejectWithValue(
                error.response ? error.response.data : { error: 'network_error' }
            );
        }
    }
);

/**
 * Ends the session server-side, so a copied cookie is dead too rather than
 * merely cleared in this browser.
 */
export const signOut = createAsyncThunk(
    'pageLayout/signOut',
    async () => {
        try {
            await apiCall('auth/logout', {});
        } catch (error) {
            // Already gone server-side; sign out locally regardless.
        }
        return true;
    }
);

export const pageLayoutSlice = createSlice({
    name: 'pageLayout',
    initialState,
    
    reducers: {
        setUserName: (state, action) => {
            state.credentials.userName = action.payload;
        },
        
        setUserId: (state, action) => {
            state.credentials.userId = action.payload;
        },

        setUserCredentials: (state, action) => { //set users credentials received from logging in
            state.loading = '';
            state.isAuthenticated = true;
            state.authChecked = true;
            state.sessionMessage = '';
            state.credentials.userName = action.payload.userName;
            state.credentials.userId = action.payload.userId;
            state.credentials.email = action.payload.email;
            state.credentials.userStatus = action.payload.userStatus || '';
            state.credentials.mustChangePassword = !!action.payload.mustChangePassword;
            if( action.payload.role ){
                state.credentials.userType.role = action.payload.role;
            }
        },

        /**
         * Returns the app to the login screen when the session ends without
         * the user asking - it idled out, an admin deactivated the account, or
         * the password was changed in another browser.
         */
        sessionLost: (state, action) => {
            const reason = action.payload && action.payload.error;
            let message = '';
            if( reason === 'session_expired' ){
                message = 'Your session has expired. Please sign in again.';
            } else if ( reason === 'account_inactive' ){
                message = 'This account is no longer active. Contact an administrator.';
            }

            const fresh = deepCopy(initialState);
            fresh.authChecked = true;
            fresh.sessionMessage = message;

            // mustChangePassword is not a lost session - the user stays signed
            // in, they are simply confined to the change-password screen.
            if( reason === 'password_change_required' ){
                state.credentials.mustChangePassword = true;
                return;
            }

            return fresh;
        },

        passwordChanged: (state) => {
            state.credentials.mustChangePassword = false;
            state.loading = 'none';
        },

        setUserType: (state, action) => {
            state.credentials.userType.type = action.payload.userType;
            state.credentials.userType.permissions = action.payload.permissions;
        },

        setCurrentPage: (state, action) => {
            for ( let page in state.pages ){
                if ( state.pages.hasOwnProperty( page ) ){
                    state.pages[ page ] = false;
                }
            }
            state.pages[ action.payload ] = true;
            state.currentSubTitle = state.subTitles[ action.payload ] || '';
        },

        setPages: (state, action) => {
            action.payload.forEach( page => state.pages[ page ] = false );
        },

        setSidePanelTitle: (state, action) => {
            state.sidePanelTitle = action.payload;
        },

        setSidePanelOptions: (state, action) => {
            state.sidePanelOptions = action.payload;
        },

        setAdminPortalView: (state, action) => {
            for ( let view in state.adminPortalView ){
                if ( state.adminPortalView.hasOwnProperty( view ) ){
                    state.adminPortalView[ view ] = false;
                }
            }
            state.adminPortalView[ action.payload ] = true;
        },

        setHomeAccountId: (state, action) => {
            state.credentials.homeAccountId = action.payload;
        },

        handleSignOut: (state) =>{
            state = deepCopy(initialState);
        },

        setLoginLoadingStatus: (state, action) =>{
            state.loading = action.payload;
        }
    },

    extraReducers: (builder) => {
        builder.addCase(restoreSession.pending, (state) => {

            state.loading = 'loading';

        }).addCase(restoreSession.fulfilled, (state, action) => {

            const user = action.payload.user;
            state.isAuthenticated = true;
            state.authChecked = true;
            state.loading = 'none';
            state.credentials.userName = user.userName;
            state.credentials.userId = user.userId;
            state.credentials.email = user.email;
            state.credentials.userStatus = user.userStatus || '';
            state.credentials.userType.role = user.role;
            state.credentials.mustChangePassword = !!user.mustChangePassword;

        }).addCase(restoreSession.rejected, (state) => {

            // No valid session. This is the ordinary first-visit path.
            state.isAuthenticated = false;
            state.authChecked = true;
            state.loading = 'none';

        }).addCase(signOut.fulfilled, () => {

            const fresh = deepCopy(initialState);
            fresh.authChecked = true;
            return fresh;

        }).addCase(getUserTypeAndPermissions.pending, (state) => {

            state.loading = 'loading';

        }).addCase(getUserTypeAndPermissions.fulfilled, (state, action) => {

            state.credentials.userType.userType = action.payload.userType || 'N/A';
            const permissionsStrings = action.payload.permissions || [];
            const permissions = [];
            const pages = [];
            permissionsStrings.forEach( permissionString => {
                let permissionIndex = state.permissions.findIndex( permission => permission.includes(permissionString) );
                if( permissionIndex !== -1 ){
                    let splitArr = state.permissions[ permissionIndex ].split('.');
                    pages.push( splitArr[0] );
                    permissions.push( splitArr[1] );
                }
            });
            state.credentials.userType.permissions = permissions;
            state.credentials.userType.pages = [...new Set(pages)];
            state.credentials.userStatus = action.payload.userStatus || '';
            state.credentials.userType.role = action.payload.role;
            state.loading = 'none';

            // Check if a page is already set (e.g., from email token navigation)
            const hasActivePage = Object.values(state.pages).some(isActive => isActive);

            if( action.payload.userType ){
                let page;
                switch( action.payload.userType ){
                    case "N/A":
                    case "Pending":
                        page = "Pending";
                    break;
                    default:
                        page = "Quotes";
                }
                if( action.payload.userStatus && action.payload.userStatus.toLowerCase() === 'deactivated' ){
                    state.pages['Deactivated'] = true;
                    state.currentSubTitle = state.subTitles['Deactivated'];
                } else if( !hasActivePage ) {
                    // Only set default page if no page is already active
                    state.pages[page] = true;
                    state.currentSubTitle = state.subTitles[page];
                }

            } else {
                state.credentials.userType.pages = [];
                // state.pages['Deactivated'] = true;
                // state.currentSubTitle = state.subTitles['Deactivated'];
            }

        }).addCase(getUserTypeAndPermissions.rejected, (state) => {

            state.loading = 'rejected';

        }).addCase(putUser.pending, (state) => {

            state.loading = 'loading';

        }).addCase(putUser.fulfilled, (state, action) => {
            
            state.credentials.userType.userType = "Pending";
            state.credentials.userType.permissions = [];
            state.credentials.userType.pages = ['Pending'];
            state.loading = 'none';

        }).addCase(putUser.rejected, (state) => {

            state.loading = 'rejected';

        }).addCase(putUserStatus.pending, (state) => {

            state.userStatusLoading = 'loading';

        }).addCase(putUserStatus.fulfilled, (state, action) => {
            if( action.payload === true ){
                state.credentials.userStatus = action.meta.arg.status;
            }
            state.userStatusLoading = 'complete';

        }).addCase(putUserStatus.rejected, (state) => {

            state.userStatusLoading = 'rejected';

        }).addCase(userLogin.pending, (state) => {

            state.loading = 'loading';

        }).addCase(userLogin.fulfilled, (state, action) => {
            if( action.payload.success === true ){
                state.credentials.userId = action.payload.userId.toString();
                state.credentials.email = action.payload.email;
                state.credentials.userStatus = action.payload.userStatus;
                state.credentials.userName = action.payload.userName;
                // state.loading = 'complete';
                state.isAuthenticated = true;
            } else {
                if( action.payload.error === true ){
                    state.loading = 'rejected';
                } else {
                    state.loading = 'invalid';
                }
            }

        }).addCase(userLogin.rejected, (state) => {

            state.loading = 'rejected';

        });
    }
});

export const {
    setCurrentPage,
    setUserCredentials,
    setUserName,
    setUserId,
    setUserType,
    setSidePanelTitle,
    setAdminPortalView,
    setSidePanelOptions,
    setHomeAccountId,
    setLoginLoadingStatus,
    sessionLost,
    passwordChanged
} = pageLayoutSlice.actions;

/* navigation and page layout */
export const selectNavState = state => state.pageLayout.pages;
export const selectCurrentPage = state => Object.keys(state.pageLayout.pages).filter((page) => state.pageLayout.pages[ page ])[0];
export const selectCurrentSubTitle = state => state.pageLayout.currentSubTitle;
export const selectSidePanelTitle = state => state.pageLayout.sidePanelTitle;
export const selectAdminPortalView = state => state.pageLayout.adminPortalView;
export const selectLoadingState = state => state.pageLayout.loading;
export const selectSidePanelOptions = state => state.pageLayout.sidePanelOptions;
export const selectUserStatusLoading = state => state.pageLayout.userStatusLoading;
export const selectIsAuthenticated = state => state.pageLayout.isAuthenticated;
export const selectAuthChecked = state => state.pageLayout.authChecked;
export const selectSessionMessage = state => state.pageLayout.sessionMessage;
export const selectMustChangePassword = state => state.pageLayout.credentials.mustChangePassword;

/* user credentials */
export const selectUserName = state => state.pageLayout.credentials.userName;
export const selectUserId = state => state.pageLayout.credentials.userId;
export const selectUserType = state => state.pageLayout.credentials.userType;
export const selectUserEmail = state => state.pageLayout.credentials.email;
export const selectPages = state => state.pageLayout.credentials.userType.pages;
export const selectRole = state => state.pageLayout.credentials.userType.role;
export const selectUserPermissions = state => state.pageLayout.credentials.userType.permissions;
export const selectCredentials = state => state.pageLayout.credentials;

export default pageLayoutSlice.reducer;